import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '@/contexts/BusinessContext';
import { getBusinesses, createBusiness, updateBusiness, deleteBusiness } from '@/lib/api';
import { encryptStripeKey, deleteStripeKey } from '@/lib/encryption';
import { fetchStripeAccount } from '@/lib/stripeService';
import type { Business } from '@/types/database';
import { Building2, Plus, LogOut, RefreshCw, Eye, EyeOff, X, Pencil, Trash2 } from 'lucide-react';
import LogoDropzone from '@/components/LogoDropzone';
import { supabase } from '@/lib/supabase';
import LogoSpinner from '@/components/LogoSpinner';

function AccountingBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105 blur-md"
        style={{ backgroundImage: "url('/assets/images/Background-6.png')" }}
      />
      <div className="absolute inset-0 bg-black/25" />
    </div>
  );
}

export default function BusinessesPage() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [loadingStripeData, setLoadingStripeData] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [formData, setFormData] = useState({ name: '', stripeKey: '', bankRate: '15.42', blackMarketRate: '20.25', logoUrl: '' });
  const { setSelectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const [transitioning, setTransitioning] = useState(false);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => { loadBusinesses(); }, []);

  async function loadBusinesses() {
    try { const data = await getBusinesses(); setBusinesses(data); }
    catch (error: any) { alert(error.message); }
    finally { setLoading(false); }
  }

  function openAddModal() {
    setEditingBusiness(null);
    setFormData({ name: '', stripeKey: '', bankRate: '15.42', blackMarketRate: '20.25', logoUrl: '' });
    setShowStripeKey(false);
    setModalVisible(true);
  }

  function openEditModal(business: Business) {
    setEditingBusiness(business);
    setFormData({ name: business.name, stripeKey: '', bankRate: business.bank_rate.toString(), blackMarketRate: business.default_black_market_rate.toString(), logoUrl: business.logo_url || '' });
    setShowStripeKey(false);
    setModalVisible(true);
  }

  async function handleImportFromStripe() {
    if (!formData.stripeKey.trim()) { alert('Please enter a Stripe secret key first'); return; }
    try {
      setLoadingStripeData(true);
      const accountData = await fetchStripeAccount(formData.stripeKey);
      setFormData({
        ...formData,
        name: accountData.business_profile.name || accountData.email || formData.name,
        logoUrl: accountData.settings.branding.logo || accountData.settings.branding.icon || formData.logoUrl,
      });
      alert('Business data imported from Stripe');
    } catch (error: any) {
      alert(error.message || 'Failed to import data from Stripe');
    } finally { setLoadingStripeData(false); }
  }

  async function handleSave() {
    if (!formData.name.trim()) { alert('Please enter a business name'); return; }
    if (!editingBusiness && !formData.stripeKey.trim()) { alert('Please enter a Stripe secret key'); return; }
    try {
      setLoading(true);
      if (editingBusiness) {
        const updates: any = { name: formData.name, bank_rate: parseFloat(formData.bankRate), default_black_market_rate: parseFloat(formData.blackMarketRate), logo_url: formData.logoUrl || null };
        if (formData.stripeKey.trim()) {
          const encryptedKey = await encryptStripeKey(editingBusiness.id, formData.stripeKey);
          updates.stripe_key_encrypted = encryptedKey;
        }
        await updateBusiness(editingBusiness.id, updates);
      } else {
        const tempId = Date.now().toString();
        const encryptedKey = await encryptStripeKey(tempId, formData.stripeKey);
        const newBusiness = await createBusiness(formData.name, encryptedKey, parseFloat(formData.bankRate), parseFloat(formData.blackMarketRate));
        const finalEncryptedKey = await encryptStripeKey(newBusiness.id, formData.stripeKey);
        await updateBusiness(newBusiness.id, { stripe_key_encrypted: finalEncryptedKey, logo_url: formData.logoUrl || null });
      }
      await loadBusinesses();
      setModalVisible(false);
      setEditingBusiness(null);
      setFormData({ name: '', stripeKey: '', bankRate: '15.42', blackMarketRate: '20.25', logoUrl: '' });
    } catch (error: any) { alert(error.message); }
    finally { setLoading(false); }
  }

  async function handleDelete(business: Business) {
    if (!confirm(`Are you sure you want to delete "${business.name}"?`)) return;
    try {
      setLoading(true);
      await deleteStripeKey(business.stripe_key_encrypted);
      await deleteBusiness(business.id);
      await loadBusinesses();
    } catch (error: any) { alert(error.message); }
    finally { setLoading(false); }
  }

  const setCardRef = useCallback((id: string) => (el: HTMLDivElement | null) => {
    cardRefs.current[id] = el;
  }, []);

  function selectBusiness(business: Business) {
    if (transitioning) return;
    setTransitioning(true);
    setActiveCardId(business.id);

    setTimeout(() => {
      setSelectedBusiness(business);
      navigate('/', { replace: true });
    }, 500);
  }

  async function handleSignOut() { await supabase.auth.signOut(); navigate('/auth', { replace: true }); }

  if (loading && businesses.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center">
        <LogoSpinner size={48} />
      </div>
    );
  }

  return (
    <div
      className="h-screen flex flex-col relative"
      style={{
        transform: transitioning ? 'translateX(-100%)' : 'translateX(0)',
        opacity: transitioning ? 0 : 1,
        transition: transitioning
          ? 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1), opacity 400ms ease-out'
          : undefined,
      }}
    >
      <AccountingBackground />

      <div className="liquid-glass px-4 sm:px-8 py-4 sm:py-5 shrink-0 mx-4 sm:mx-8 mt-4 relative z-10">
        <div className="flex justify-between items-center gap-1">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-white tracking-tight">Companies</h1>
            <p className="text-xs text-white/60 hidden sm:block">Select and Manage Your Company</p>
          </div>
          <button onClick={handleSignOut} className="flex items-center gap-2 px-3 sm:px-4 py-2 text-white/60 hover:text-red-400 glass-hover-red rounded-xl text-sm font-medium shrink-0">
            <LogOut size={16} />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 sm:px-8 py-6 sm:py-8 relative flex items-center justify-center z-10">
        <div className="max-w-5xl mx-auto relative z-10">
          {businesses.length === 0 ? (
            <div className="flex flex-col items-center justify-center">
              <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
                <Building2 size={32} className="text-white/70" />
              </div>
              <h2 className="text-lg font-bold text-white mb-1">No businesses yet</h2>
              <p className="text-sm text-white/60 mb-6">Create your first business to get started</p>
              <button onClick={openAddModal} className="flex items-center gap-2 px-5 py-2.5 glass-button-active text-white rounded-xl text-sm font-semibold">
                <Plus size={16} />
                Add Business
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-5">
              {businesses.map((business) => {
                const isActive = activeCardId === business.id;
                return (
                  <div
                    key={business.id}
                    ref={setCardRef(business.id)}
                    className={`liquid-glass p-5 group cursor-pointer transition-all duration-300 ${
                      isActive
                        ? 'scale-95 ring-2 ring-teal-400/50'
                        : transitioning
                          ? 'opacity-40 scale-95'
                          : 'hover:liquid-glass-hover hover:-translate-y-1.5 active:scale-95'
                    }`}
                    onClick={() => selectBusiness(business)}
                  >
                    <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button onClick={(e) => { e.stopPropagation(); openEditModal(business); }}
                        className="p-2 bg-white/10 text-white/70 rounded-lg glass-hover"><Pencil size={13} /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(business); }}
                        className="p-2 bg-red-500/10 text-red-400 rounded-lg glass-hover-red"><Trash2 size={13} /></button>
                    </div>
                    <div className="flex flex-col items-center mb-4">
                      <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center ring-1 ring-white/20 bg-white/10">
                        {business.logo_url ? (
                          <img src={business.logo_url} alt={business.name} className="w-full h-full object-cover" />
                        ) : (
                          <Building2 size={32} className="text-white/50" />
                        )}
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-white text-center truncate">{business.name}</h3>
                    <p className="text-[11px] text-white/60 text-center mt-0.5">Click to manage</p>
                  </div>
                );
              })}

              <button onClick={openAddModal}
                className="liquid-glass p-5 flex flex-col items-center justify-center gap-2 hover:liquid-glass-hover hover:-translate-y-1.5 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors duration-300">
                  <Plus size={22} className="text-white/60 group-hover:text-white transition-colors duration-300" />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {modalVisible && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto ">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-bold text-white tracking-tight">{editingBusiness ? 'Edit Business' : 'Add Business'}</h2>
              <button onClick={() => setModalVisible(false)} className="p-1.5 glass-hover rounded-lg">
                <X size={18} className="text-white" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1.5">Business Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm bg-white/10 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40" placeholder="Business name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1.5">Logo</label>
                <LogoDropzone
                  value={formData.logoUrl}
                  onChange={(dataUrl) => setFormData({ ...formData, logoUrl: dataUrl })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1.5">{editingBusiness ? 'Stripe Key (leave empty to keep)' : 'Stripe Secret Key'}</label>
                <div className="relative">
                  <input type={showStripeKey ? 'text' : 'password'} value={formData.stripeKey}
                    onChange={(e) => setFormData({ ...formData, stripeKey: e.target.value })}
                    className="w-full px-4 py-2.5 pr-11 text-sm bg-white/10 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40" placeholder="sk_test_..." />
                  <button type="button" onClick={() => setShowStripeKey(!showStripeKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white hover:text-white transition-colors">
                    {showStripeKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {formData.stripeKey.trim() && (
                <button onClick={handleImportFromStripe} disabled={loadingStripeData}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl glass-hover text-sm font-medium disabled:opacity-50">
                  {loadingStripeData ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><RefreshCw size={14} /><span>Import from Stripe</span></>}
                </button>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">Bank Rate (MVR)</label>
                  <input type="number" step="0.01" value={formData.bankRate} onChange={(e) => setFormData({ ...formData, bankRate: e.target.value })}
                    className="w-full px-4 py-2.5 text-sm bg-white/10 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200" placeholder="15.42" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">Black Market Rate</label>
                  <input type="number" step="0.01" value={formData.blackMarketRate} onChange={(e) => setFormData({ ...formData, blackMarketRate: e.target.value })}
                    className="w-full px-4 py-2.5 text-sm bg-white/10 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200" placeholder="20.25" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setModalVisible(false)} className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium">Cancel</button>
              <button onClick={handleSave} className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-semibold">{editingBusiness ? 'Save Changes' : 'Add Business'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
